// src/screens/s2/CapitalAllocation.tsx
// ─────────────────────────────────────────────────────────────────────────────
// Session 2 Screen 6 — Capital Allocation.
// 1. Monthly available-cash waterfall from revenue down to remaining headroom.
// 2. Cascade of commitments: IMMEDIATE (salary/div/pension/CT/VAT reserve),
//    CLEARANCE (historic CT/VAT/PAYE), CONDITIONAL DLA branch (overdrawn or
//    credit), BUILDING (business EF, optional DLA deployment).
// 3. Gantt-style timeline of every clearing date.
// ─────────────────────────────────────────────────────────────────────────────

import React from 'react';
import {
  ComposedChart, Bar, Line, Cell, XAxis, YAxis, Tooltip, CartesianGrid,
  ResponsiveContainer, ReferenceLine, BarChart,
} from 'recharts';
import { ClientData } from '../../types/ClientData';
import { useXeroData } from '../../hooks/useXeroData';
import { useXeroBalance } from '../../hooks/useXeroBalance';
import { effectiveCTRate, TAX_YEARS, getTaxYearForDate } from '../../taxConfig';
import { formatCurrency, formatMonths } from '../../utils/formatters';
import { useCommaInput } from '../../utils/useCommaInput';

interface Props {
  data: ClientData;
  onUpdate: (updates: Partial<ClientData>) => void;
}

// Monthly input card bound to a ClientData field via onUpdate. Uses the
// existing useCommaInput helper so adviser gets live formatting.
function MonthlyInputCard({
  label,
  value,
  onChange,
  subtitle,
  accent,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  subtitle: string;
  accent?: string;
}) {
  const input = useCommaInput(value, onChange, true);
  return (
    <div
      className="rounded-2xl p-4"
      style={{
        backgroundColor: accent ? `${accent}10` : '#FFFFFF',
        border: `1px solid ${accent ? `${accent}40` : '#E5E7EB'}`,
      }}
    >
      <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">{label}</div>
      <div className="flex items-baseline gap-1">
        <span className="text-gray-400">£</span>
        <input
          {...input}
          placeholder="0"
          className="text-2xl font-bold text-ma-navy bg-transparent border-none p-0 focus:outline-none w-full"
        />
        <span className="text-xs text-gray-400">/mo</span>
      </div>
      <p className="text-xs text-gray-500 mt-2">{subtitle}</p>
    </div>
  );
}

function ReadonlyCard({ label, value, subtitle }: { label: string; value: string; subtitle?: string }) {
  return (
    <div className="rounded-2xl bg-gray-50 border border-gray-200 p-4">
      <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">{label}</div>
      <div className="text-2xl font-bold text-ma-navy">{value}</div>
      {subtitle && <p className="text-xs text-gray-500 mt-2">{subtitle}</p>}
    </div>
  );
}

// Personal dividend rate determined from total income against the active tax
// year's bands. Used for the DLA dividend-repayment option calc.
function dividendTaxRateFor(data: ClientData): number {
  const taxYear = data.taxYearOverride ?? getTaxYearForDate(data.sessionDate || new Date().toISOString());
  const r = TAX_YEARS[taxYear];
  const totalIncome =
    (data.salary || 0) + (data.dividends || 0) + (data.rentalIncomeNet || 0) +
    (data.savingsInterest || 0) + (data.otherDividends || 0) + (data.payeIncome || 0);
  // UK bands: basic ≤£50,270, higher to £125,140, additional above.
  // higherRateLimit in taxConfig is the TOP of the higher band / start of additional.
  if (totalIncome > r.higherRateLimit) return r.dividendAdditionalRate;
  if (totalIncome > r.basicRateLimit) return r.dividendHigherRate;
  return r.dividendBasicRate;
}

const addMonths = (base: Date, months: number) => new Date(base.getFullYear(), base.getMonth() + months, base.getDate());
const ymd = (d: Date) => d.toISOString().slice(0, 10);

export default function CapitalAllocation({ data, onUpdate }: Props) {
  const xero = useXeroData(data.xeroTenantId);
  const balance = useXeroBalance(data.xeroTenantId);

  if (xero.loading || balance.loading) {
    return <div className="p-6 max-w-6xl mx-auto text-sm text-gray-500">Loading capital allocation data…</div>;
  }
  if (!xero.mapped || !xero.summary || !balance.data) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <h2 className="text-2xl font-bold text-ma-navy mb-4">Capital Allocation</h2>
        <div className="rounded-2xl bg-amber-50 border-2 border-amber-300 p-5 text-sm text-amber-800">
          This screen needs live P&amp;L + balance sheet. {xero.error || balance.error || 'Ensure the Xero tenant is connected and mapped.'}
        </div>
      </div>
    );
  }

  const s = xero.summary!;
  const b = balance.data!;

  // ── Monthly cash waterfall ─────────────────────────────────────────────
  const monthlyRevenue = s.revenue / 12;
  const monthlyCOS = s.costOfSales / 12;
  const monthlyStaff = s.staffCosts / 12;
  const monthlyOperating = s.totalOperating / 12;
  const monthlyOwnerSalary = s.ownerSalary / 12;
  const monthlyOwnerPension = s.ownerPension / 12;
  const monthlyFinanceDep = s.financeAndDepreciation / 12;
  const pbtMonthly = monthlyRevenue - monthlyCOS - monthlyStaff - monthlyOperating - monthlyOwnerSalary - monthlyOwnerPension - monthlyFinanceDep;
  const ctRate = effectiveCTRate(s.netProfit);
  const monthlyCTExposure = (s.netProfit * ctRate) / 12;
  const patMonthly = pbtMonthly - monthlyCTExposure;

  // Stacked-spacer waterfall (same pattern as BusinessPL). Revenue anchors
  // at floor=0, each deduction floats between before/after, remaining PAT
  // anchors at the foot. Negative PAT renders below the y=0 reference.
  const waterfallData: Array<{ name: string; spacer: number; value: number; running: number; fill: string }> = [];
  let wfRunning = 0;
  const wfAnchor = (name: string, fill: string) => {
    waterfallData.push({ name, spacer: Math.min(0, wfRunning), value: Math.abs(wfRunning), running: wfRunning, fill });
  };
  const wfDeduct = (name: string, amount: number, fill: string) => {
    const before = wfRunning;
    const after = before - amount;
    waterfallData.push({
      name,
      spacer: Math.min(before, after),
      value: Math.abs(before - after),
      running: after,
      fill,
    });
    wfRunning = after;
  };
  wfRunning = monthlyRevenue;
  wfAnchor('Revenue', '#1E3A5F');
  wfDeduct('−CoS', monthlyCOS, '#DC2626');
  wfDeduct('−Staff', monthlyStaff, '#DC2626');
  wfDeduct('−Operating', monthlyOperating, '#DC2626');
  wfDeduct('−Salary', monthlyOwnerSalary, '#DC2626');
  wfDeduct('−Pension', monthlyOwnerPension, '#DC2626');
  wfDeduct('−F&D', monthlyFinanceDep, '#DC2626');
  wfDeduct('−CT', monthlyCTExposure, '#DC2626');
  wfAnchor('Remaining PAT', patMonthly >= 0 ? '#C9A84C' : '#DC2626');

  // ── Immediate commitments (inputs bound via onUpdate) ──────────────────
  const s2Dividend = data.s2DividendMonthly ?? 0;
  const s2Pension = data.s2PensionMonthly ?? monthlyOwnerPension;
  const s2CtReserve = data.s2CtReserveMonthly ?? monthlyCTExposure;
  const s2VatReserve = data.s2VatReserveMonthly ?? 0;
  const showVatReserve = b.vatLiability > 0;

  // Remaining after immediate commitments (salary+pension+F&D+CT already in
  // the PAT figure via the waterfall; immediate layer deducts dividends + the
  // reserve SOs advisers choose). Negative remaining triggers a growth flag.
  const immediateDeductions = s2Dividend + s2CtReserve + (showVatReserve ? s2VatReserve : 0);
  const remaining = patMonthly - immediateDeductions;

  // ── Clearance plan for historic liabilities ────────────────────────────
  const historicCT = Math.max(0, b.ctLiability);
  const historicVAT = Math.max(0, b.vatLiability);
  const historicPAYE = Math.max(0, b.payeLiability);
  const historicTotal = historicCT + historicVAT + historicPAYE;
  const clearanceMonths = data.s2HistoricCtLiabilityClearanceMonths ?? 12;
  const monthlyHistoricClearance = clearanceMonths > 0 ? historicTotal / clearanceMonths : 0;

  // ── DLA branch ──────────────────────────────────────────────────────────
  // Use ClientData override when present, otherwise the live BS figure.
  const dlaBalance = data.businessDlaBalance ?? b.dlaBalance;
  const dlaOverdrawn = dlaBalance < 0;
  const dlaOwed = dlaOverdrawn ? Math.abs(dlaBalance) : 0;
  const dlaCredit = dlaBalance > 0 ? dlaBalance : 0;

  // Dividend-route calc (if overdrawn): gross dividend needed so the
  // post-tax amount clears the DLA balance. Personal tax rate derived
  // from the client's current income band.
  const divRate = dividendTaxRateFor(data);
  const grossDividendForDla = dlaOwed / (1 - divRate);
  const personalTaxCost = grossDividendForDla * divRate;
  const formalRepaymentMonths = data.s2DlaMonthlyRepayment && data.s2DlaMonthlyRepayment > 0
    ? Math.ceil(dlaOwed / data.s2DlaMonthlyRepayment)
    : 12;
  const formalMonthly = formalRepaymentMonths > 0 ? dlaOwed / formalRepaymentMonths : 0;
  const route = data.s2DlaRepaymentRoute ?? null;

  // ── Business EF ─────────────────────────────────────────────────────────
  // EF target covers fixed costs (staff + operating + owner salary) for the
  // adviser-chosen number of months. Current buffer lives on ClientData.
  const efTargetMonths = data.s2BusinessEfTarget ?? 3;
  const fixedCostsMonthly = monthlyStaff + monthlyOperating + monthlyOwnerSalary;
  const efTarget = fixedCostsMonthly * efTargetMonths;
  const efCurrent = data.businessOperatingBufferBalance || 0;
  const efGap = Math.max(0, efTarget - efCurrent);
  const efMonthlyBuild = Math.max(0, remaining - monthlyHistoricClearance);
  const efMonthsToComplete = efMonthlyBuild > 0 ? Math.ceil(efGap / efMonthlyBuild) : 0;

  // ── Timeline: single horizontal axis, bar = months from now until clear ─
  const today = new Date();
  const timeline: Array<{ name: string; start: number; end: number; fill: string }> = [];
  if (historicTotal > 0) {
    timeline.push({ name: 'Historic liabilities cleared', start: 0, end: clearanceMonths, fill: '#DC2626' });
  }
  if (efGap > 0 && efMonthsToComplete > 0) {
    timeline.push({ name: 'Business EF complete', start: 0, end: efMonthsToComplete, fill: '#10B981' });
  }
  if (dlaOverdrawn) {
    const months = route === 'dividend' ? 1 : formalRepaymentMonths;
    timeline.push({ name: 'DLA repaid', start: 0, end: months, fill: '#F59E0B' });
  }
  // Recharts stacked-bar "spacer + value" pattern for Gantt visuals.
  const timelineChartData = timeline.map((t) => ({
    name: t.name,
    spacer: t.start,
    duration: t.end - t.start,
    fill: t.fill,
    dateLabel: ymd(addMonths(today, t.end)),
  }));
  const maxMonthsOnTimeline = timeline.length > 0 ? Math.max(...timeline.map((t) => t.end)) + 2 : 12;

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* HEADER */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-ma-navy">
          Capital Allocation
          {xero.tenantName && <span className="ml-2 text-sm font-normal text-gray-500">— {xero.tenantName}</span>}
        </h2>
        <p className="text-gray-500 mt-1 text-sm">
          Where every pound of monthly cash is going — now, on clearance, and on
          building the buffer.
        </p>
      </div>

      {/* SECTION A — MONTHLY CASH WATERFALL */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-6">
        <h3 className="text-sm font-bold text-ma-navy uppercase tracking-wide mb-3">
          Monthly cash available
        </h3>
        <div style={{ width: '100%', height: 280 }}>
          <ResponsiveContainer>
            <ComposedChart data={waterfallData} margin={{ top: 20, right: 20, bottom: 8, left: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="name" tick={{ fontSize: 10 }} stroke="#9CA3AF" />
              <YAxis tick={{ fontSize: 10 }} stroke="#9CA3AF" tickFormatter={(v) => `£${Math.round(v / 1000)}k`} />
              <Tooltip formatter={(v: any) => formatCurrency(Number(v))} />
              <ReferenceLine y={0} stroke="#9CA3AF" />
              {/* Stacked-spacer waterfall: transparent spacer + coloured value. */}
              <Bar dataKey="spacer" stackId="mwf" fill="transparent" />
              <Bar dataKey="value" stackId="mwf" fill="#1E3A5F" radius={[4, 4, 0, 0]}>
                {waterfallData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
              </Bar>
              <Line
                type="monotone"
                dataKey="running"
                stroke="#94A3B8"
                strokeWidth={2}
                strokeDasharray="4 2"
                dot={{ r: 3, fill: '#94A3B8' }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
        <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t border-gray-100">
          <div>
            <div className="text-xs text-gray-500">Revenue</div>
            <div className="text-lg font-bold text-ma-navy">{formatCurrency(monthlyRevenue)}/mo</div>
          </div>
          <div>
            <div className="text-xs text-gray-500">Post-tax profit (PAT)</div>
            <div className="text-lg font-bold text-ma-navy">{formatCurrency(patMonthly)}/mo</div>
          </div>
          <div>
            <div className="text-xs text-gray-500">After commitments</div>
            <div className={`text-lg font-bold ${remaining >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
              {formatCurrency(remaining)}/mo
            </div>
          </div>
        </div>
        {remaining < 0 && (
          <div className="mt-4 rounded-xl bg-red-50 border-2 border-red-300 p-4 text-sm text-red-800">
            <strong>Remaining cash is negative.</strong> The business needs to grow
            before this plan is fully sustainable. See the Value Gap Calculator
            for the revenue / margin lift needed to close this.
          </div>
        )}
      </div>

      {/* SECTION B — CASCADE */}
      <div className="mb-6">
        <h3 className="text-sm font-bold text-ma-navy uppercase tracking-wide mb-3">Immediate — runs now</h3>
        <div className={`grid ${showVatReserve ? 'grid-cols-5' : 'grid-cols-4'} gap-3 mb-6`}>
          <ReadonlyCard
            label="Salary"
            value={`${formatCurrency(monthlyOwnerSalary)}/mo`}
            subtitle="S1 agreed"
          />
          <MonthlyInputCard
            label="Dividends"
            value={s2Dividend}
            onChange={(v) => onUpdate({ s2DividendMonthly: v })}
            subtitle="Personal drawings"
          />
          <MonthlyInputCard
            label="Pension"
            value={s2Pension}
            onChange={(v) => onUpdate({ s2PensionMonthly: v })}
            subtitle="Employer contribution"
            accent="#10B981"
          />
          <MonthlyInputCard
            label="CT reserve"
            value={s2CtReserve}
            onChange={(v) => onUpdate({ s2CtReserveMonthly: v })}
            subtitle={`Cover @ ${(ctRate * 100).toFixed(1)}%`}
            accent="#60A5FA"
          />
          {showVatReserve && (
            <MonthlyInputCard
              label="VAT reserve"
              value={s2VatReserve}
              onChange={(v) => onUpdate({ s2VatReserveMonthly: v })}
              subtitle={`Live balance ${formatCurrency(b.vatLiability)}`}
              accent="#60A5FA"
            />
          )}
        </div>

        <h3 className="text-sm font-bold text-ma-navy uppercase tracking-wide mb-3">Clearance — historic liabilities</h3>
        <div className="grid grid-cols-4 gap-3 mb-6">
          <ReadonlyCard label="CT outstanding" value={formatCurrency(historicCT)} />
          <ReadonlyCard label="VAT outstanding" value={formatCurrency(historicVAT)} />
          <ReadonlyCard label="PAYE outstanding" value={formatCurrency(historicPAYE)} />
          <div className="rounded-2xl border border-gray-200 p-4 bg-white">
            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Clear over</div>
            <div className="flex items-baseline gap-2">
              <input
                type="number"
                min={1}
                value={clearanceMonths}
                onChange={(e) => onUpdate({ s2HistoricCtLiabilityClearanceMonths: Math.max(1, parseInt(e.target.value || '1', 10)) })}
                className="text-2xl font-bold text-ma-navy bg-transparent border border-gray-300 rounded px-2 py-1 w-20"
              />
              <span className="text-xs text-gray-500">months</span>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              = {formatCurrency(monthlyHistoricClearance)}/mo
            </p>
          </div>
        </div>

        {/* DLA CONDITIONAL BRANCH */}
        {dlaOverdrawn && (
          <>
            <h3 className="text-sm font-bold uppercase tracking-wide mb-3" style={{ color: '#F97316' }}>
              Director's Loan — overdrawn {formatCurrency(dlaOwed)}
            </h3>
            <div className="grid grid-cols-2 gap-4 mb-6">
              {/* Option A — dividend route */}
              <div
                className={`rounded-2xl border-2 p-5 cursor-pointer transition-colors ${route === 'dividend' ? 'border-ma-navy bg-blue-50' : 'border-gray-200 bg-white hover:border-gray-300'}`}
                onClick={() => onUpdate({ s2DlaRepaymentRoute: 'dividend' })}
              >
                <div className="flex items-start gap-3 mb-3">
                  <input
                    type="radio"
                    checked={route === 'dividend'}
                    onChange={() => onUpdate({ s2DlaRepaymentRoute: 'dividend' })}
                    className="mt-1"
                  />
                  <div>
                    <div className="text-sm font-bold text-ma-navy">Option A — Dividend route</div>
                    <div className="text-xs text-gray-500">Declare gross dividend, use net to clear DLA</div>
                  </div>
                </div>
                <div className="space-y-1.5 text-sm">
                  <div className="flex justify-between"><span className="text-gray-500">Gross dividend:</span><span className="font-semibold">{formatCurrency(grossDividendForDla)}</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">Personal tax @ {(divRate * 100).toFixed(2)}%:</span><span className="font-semibold text-red-700">−{formatCurrency(personalTaxCost)}</span></div>
                  <div className="flex justify-between pt-1 border-t border-gray-200"><span className="text-gray-500">Net to DLA:</span><span className="font-semibold">{formatCurrency(dlaOwed)}</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">Months to clear:</span><span className="font-semibold">{formatMonths(1)} month</span></div>
                </div>
              </div>
              {/* Option B — formal repayment */}
              <div
                className={`rounded-2xl border-2 p-5 cursor-pointer transition-colors ${route === 'formal' ? 'border-ma-navy bg-blue-50' : 'border-gray-200 bg-white hover:border-gray-300'}`}
                onClick={() => onUpdate({ s2DlaRepaymentRoute: 'formal' })}
              >
                <div className="flex items-start gap-3 mb-3">
                  <input
                    type="radio"
                    checked={route === 'formal'}
                    onChange={() => onUpdate({ s2DlaRepaymentRoute: 'formal' })}
                    className="mt-1"
                  />
                  <div>
                    <div className="text-sm font-bold text-ma-navy">Option B — Formal repayment</div>
                    <div className="text-xs text-gray-500">Monthly transfer from personal to company</div>
                  </div>
                </div>
                <div className="space-y-1.5 text-sm">
                  <div className="flex justify-between"><span className="text-gray-500">Monthly repayment:</span><span className="font-semibold">{formatCurrency(formalMonthly)}</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">Months to clear:</span><span className="font-semibold">{formatMonths(formalRepaymentMonths)} months</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">Personal cash impact:</span><span className="font-semibold text-red-700">−{formatCurrency(formalMonthly)}/mo</span></div>
                  <div className="flex justify-between pt-1 border-t border-gray-200"><span className="text-gray-500">No personal tax:</span><span className="font-semibold text-emerald-700">£0</span></div>
                </div>
              </div>
            </div>
          </>
        )}

        {dlaCredit > 0 && (
          <>
            <h3 className="text-sm font-bold uppercase tracking-wide mb-3" style={{ color: '#10B981' }}>
              Director's Loan — credit {formatCurrency(dlaCredit)} (company owes you)
            </h3>
            <div className="grid grid-cols-3 gap-3 mb-6">
              {[
                { key: 'dividend' as const, title: 'Extract tax-free', body: `Draw down DLA as repayment of director's loan — no personal tax.` },
                { key: 'formal' as const, title: 'Leave in company', body: `Keep as working capital. No tax event. Extract later at a lower income year.` },
                { key: 'credit-reduce-dividends' as const, title: 'Reduce dividends', body: `Deploy against the dividend SO — reduces future personal tax burden.` },
              ].map((opt) => (
                <div
                  key={opt.key}
                  className={`rounded-2xl border-2 p-4 cursor-pointer transition-colors ${route === opt.key ? 'border-emerald-500 bg-emerald-50' : 'border-gray-200 bg-white hover:border-gray-300'}`}
                  onClick={() => onUpdate({ s2DlaRepaymentRoute: opt.key })}
                >
                  <div className="flex items-start gap-2">
                    <input type="radio" checked={route === opt.key} onChange={() => onUpdate({ s2DlaRepaymentRoute: opt.key })} className="mt-1" />
                    <div>
                      <div className="text-sm font-bold text-ma-navy">{opt.title}</div>
                      <p className="text-xs text-gray-600 mt-1">{opt.body}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        <h3 className="text-sm font-bold text-ma-navy uppercase tracking-wide mb-3">Building — business EF</h3>
        <div className="grid grid-cols-4 gap-3 mb-6">
          <ReadonlyCard label="Current buffer" value={formatCurrency(efCurrent)} />
          <div className="rounded-2xl border border-gray-200 p-4 bg-white">
            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Target months</div>
            <div className="flex items-baseline gap-2">
              <input
                type="number"
                min={1}
                max={12}
                value={efTargetMonths}
                onChange={(e) => onUpdate({ s2BusinessEfTarget: Math.max(1, Math.min(12, parseInt(e.target.value || '3', 10))) })}
                className="text-2xl font-bold text-ma-navy bg-transparent border border-gray-300 rounded px-2 py-1 w-20"
              />
              <span className="text-xs text-gray-500">months cover</span>
            </div>
            <p className="text-xs text-gray-500 mt-2">= {formatCurrency(efTarget)} target</p>
          </div>
          <ReadonlyCard
            label="Monthly build"
            value={`${formatCurrency(efMonthlyBuild)}/mo`}
            subtitle="After immediate + clearance"
          />
          <ReadonlyCard
            label="Reached by"
            value={efMonthsToComplete > 0 ? ymd(addMonths(today, efMonthsToComplete)) : efGap === 0 ? 'Already met' : '—'}
            subtitle={efMonthsToComplete > 0 ? `${formatMonths(efMonthsToComplete)} months` : ''}
          />
        </div>
      </div>

      {/* SECTION C — TIMELINE */}
      {timelineChartData.length > 0 && (
        <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-6">
          <h3 className="text-sm font-bold text-ma-navy uppercase tracking-wide mb-3">
            Clearance timeline
          </h3>
          <div style={{ width: '100%', height: 60 + timelineChartData.length * 60 }}>
            <ResponsiveContainer>
              <BarChart data={timelineChartData} layout="vertical" margin={{ top: 10, right: 20, bottom: 8, left: 120 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" horizontal={false} />
                <XAxis
                  type="number"
                  tick={{ fontSize: 10 }}
                  stroke="#9CA3AF"
                  domain={[0, maxMonthsOnTimeline]}
                  tickFormatter={(v) => `+${v}mo`}
                />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} stroke="#9CA3AF" width={120} />
                <Tooltip
                  formatter={(v: any, _name: any, entry: any) => [`${v} months → ${entry.payload.dateLabel}`, entry.payload.name]}
                />
                {/* Stacked spacer + duration to fake Gantt "start offset". */}
                <Bar dataKey="spacer" stackId="tl" fill="transparent" />
                <Bar dataKey="duration" stackId="tl" fill="#1E3A5F" radius={[0, 4, 4, 0]}>
                  {timelineChartData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <p className="text-xs text-gray-500 mt-3">
            Months from today. Bars end at the clearing / completion date for
            each commitment.
          </p>
        </div>
      )}
    </div>
  );
}
